import { ChevronLeftIcon } from '@heroicons/react/outline';
import { Affix, Badge, Button, Modal, Progress } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import moment from 'moment';
import { Inter } from 'next/font/google';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import ShareButton from '@/components/PdfReader/ShareButton';
import SchoolRender from '@/components/ResourcesPage/SchoolRender';
import ChapterCard from '@/components/ResourcesPage/SchoolsPage/SchoolCard/ChapterCard';
import HomepageSlider from '@/components/V2/HomePage/HomepageSlider';
import LoginDrawer from '@/components/V2/LoginDrawer';
import SignUpDrawer from '@/components/V2/SignUpDrawer';

import {
  PostInterface,
  SchoolState,
} from '@/pages/schools/[schoolId]/[postId]';
import { programmaticScrollTo } from '@/utils/ScrollControl/scrollControl';

import { AppleLogo, GoogleIcon } from '../../../../../assets';
import {
  authModalControl,
  tokenUpdate,
} from '../../../../atoms/authModalControl';
import { authModalDrawerController } from '../../../../atoms/authModalDrawerController';
import { user } from '../../../../atoms/user';
import appwriteService from '../../../../config/appwrite';
import {
  EventDetails,
  logEvent,
} from '../../../../hooks/useClickEventTracking';
import {
  IUpdateReadingHistory,
  updateFollow,
  updateReadingHistory,
} from '../../../../services/School';
import { User } from '../../../../utils/interfaces/User';
import ButtonDefault, { ButtonVariants } from '../../../shared/basic/button';
import { ShouldRender } from '../../../shared/basic/ShouldRender';
import { getLoginToken } from '../../../shared/getToken';

const inter = Inter({
  subsets: ['latin'],
  display: 'swap',
  fallback: ['sans-serif'],
});

interface likeComponentProps {
  likeCount: number;
  isActive: boolean;
  toggleHeart?: () => void;
}

export const formatCount = (count: number): { formatted: string } => {
  let formatted: string = count.toString();

  if (count >= 1000) {
    formatted = (count / 1000).toFixed(1);
    formatted = formatted.endsWith('.0')
      ? formatted.slice(0, -2) + 'K'
      : formatted + 'K';
  }

  return { formatted };
};

interface Chapter {
  data?: PostInterface;
  schoolData: SchoolState;
}

export const LikeComponent: React.FC<likeComponentProps> = ({
  likeCount,
  isActive,
  toggleHeart,
}) => {
  const formatCountAndPadding = (
    count: number,
  ): { formatted: string; paddingClass: string } => {
    let formatted: string = count.toString();
    let paddingClass = 'pr-3';

    if (count >= 1000) {
      formatted = (count / 1000).toFixed(1);
      formatted = formatted.endsWith('.0')
        ? formatted.slice(0, -2) + 'K'
        : formatted + 'K';
    }

    const length = formatted.length;
    if (length === 3) paddingClass = 'pr-2.5';
    else if (length > 3) paddingClass = 'pr-1';

    return { formatted, paddingClass };
  };
  return (
    <div
      onClick={toggleHeart ?? undefined}
      className={clsx(
        likeCount ? 'w-[65px]' : 'w-8',
        'relative bg-[#222326]  bg-opacity-70 h-8 z-10 rounded-full cursor-pointer no-select transition-all duration-300 ease-in-out',
      )}
    >
      <div
        className={clsx(
          likeCount ? '-right-7 md:-right-8' : '-right-[59px] md:-right-[58px]',
          'absolute -top-[9px] md:-top-2 heart-animation-wrapper transition-all duration-300 ease-in-out',
        )}
      >
        <div className='stage'>
          <div className={`heart ${isActive ? 'is-active' : ''}`}></div>
        </div>
      </div>
      <ShouldRender check={likeCount}>
        <div
          className={clsx(
            'w-full customBadge flex justify-end transition-all duration-300 ease-in-out items-center pt-[4.5px]',
            formatCountAndPadding(likeCount).paddingClass,
          )}
        >
          <Badge
            count={formatCountAndPadding(likeCount).formatted}
            className='customBadge text-white'
            overflowCount={999999}
            style={{ backgroundColor: 'transparent', color: 'white' }}
          />
        </div>
      </ShouldRender>
    </div>
  );
};

const ChaptersPage: React.FC<Chapter> = ({ data, schoolData }) => {
  const [visibleDrawer, setVisibleDrawer] = useRecoilState(
    authModalDrawerController,
  );
  const [height, setHeight] = useState(0);
  const [showButton, setShowButton] = useState(false);
  const [scrolledPastThreshold, setScrolledPastThreshold] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);

  const [, setVisible] = useRecoilState(authModalControl);
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);

  const [imageBlockOffset, setImageBlockOffset] = useState<number | null>(null);

  const [scrollProgress, setScrollProgress] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [token, setToken] = useState('');
  const router = useRouter();
  const userData: User | undefined = useRecoilValue(user);
  const showAppleLogin =
    process.env.NEXT_PUBLIC_SHOW_APPLE_LOGIN === 'showLogin';
  const startTimeRef = useRef<Date | null>(null);
  const { school_id, post_id } = router.query;
  const screens = useBreakpoint();

  useEffect(() => {
    const fetchLoggedInStatus = async () => {
      const loggedInStatus = await appwriteService.isLoggedIn();
      setLoggedIn(loggedInStatus);
    };
    fetchLoggedInStatus();
    if (isTokenChanged) {
      setIsTokenChanged(false);
    }
  }, [appwriteService.account, isTokenChanged]);

  const fetchDataToken = async () => {
    try {
      const tokens = await getLoginToken();
      if (tokens) {
        handleUpdateReadingHistory({
          ecom_user_id: userData?.id as number,
          post_id: data?.postId as number,
          school_id: data?.school_id as number,
        });
      }
    } catch (error) {
      console.error('Error fetching token:', error);
    }
  };

  useEffect(() => {
    fetchDataToken();
  }, [userData]);

  // const triggerViewEvent = () => {


  //   const eventParams: EventDetails = {
  //     event_name: 'learn_view',
  //     source: 'schools',
  //     source_type: 'schools',
  //     source_id: school_id as string,
  //     sub_source: 'chapter',
  //     sub_source_id: post_id as string,
  //     unit: 'boolean',
  //     value: 1,
  //   };

  //   logEvent({
  //     eventDetails: eventParams,
  //     userData: userData,
  //     pathname: router.pathname,
  //   });
  //   startTimeRef.current = new Date();
  // };

  const triggerSessionEvent = (start: Date, end: Date) => {
    const sessionDuration = (end.getTime() - start.getTime()) / 1000;

    const eventParams: EventDetails = {
      event_name: 'learn_session',
      source: 'schools',
      source_type: 'schools',
      source_id: school_id as string,
      sub_source: 'chapter',
      sub_source_id: post_id as string,
      unit: 'seconds',
      value: sessionDuration,
      activity_start: moment(start).format('YYYY-MM-DD HH:mm:ss'),
      activity_end: moment(end).format('YYYY-MM-DD HH:mm:ss'),
    };

    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
  };

  useEffect(() => {
    // triggerViewEvent();

    return () => {
      const currentTime = new Date();
      if (startTimeRef.current) {
        triggerSessionEvent(startTimeRef.current, currentTime);
      }
    };
  }, []);

  const fetchData = async () => {
    try {
      const token = await getLoginToken();
      setToken(token as string);
    } catch (error) {
      console.error('Error fetching token:', error);
    }
  };

  useEffect(() => {
    fetchData();
    if (isTokenChanged) {
      setIsTokenChanged(false);
    }
  }, [isTokenChanged]);

  const onCloseLogin = () => {
    setVisibleDrawer((prevState) => ({
      ...prevState,
      login: false,
    }));
    setScrolledPastThreshold(true);
  };

  const onCloseSignUp = () => {
    setVisibleDrawer((prevState) => ({
      ...prevState,
      signUp: false,
    }));
    setScrolledPastThreshold(true);
  };

  useEffect(() => {
    const storedData: string[] = JSON.parse(
      localStorage.getItem('selectedChapterIds') || '[]',
    );

    const newSchoolId = data?.slug;

    if (newSchoolId && !storedData.includes(newSchoolId)) {
      storedData.push(newSchoolId);

      localStorage.setItem('selectedChapterIds', JSON.stringify(storedData));
    }
  }, [data?.slug]);

  const handleUpdateReadingHistory = async (data: IUpdateReadingHistory) => {
    try {
      await updateReadingHistory({
        school_id: data.school_id,
        post_id: data.post_id,
        ecom_user_id: data?.ecom_user_id as number,
      });
    } catch (error: any) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    }
  };

  useEffect(() => {
    programmaticScrollTo(
      {
        top: 0,
        behavior: 'auto', // instant scroll
      },
      0,
    );
  }, []);

  useEffect(() => {
    const calculateHeight = () => {
      const windowHeight = window.innerHeight;
      const fortyPercentHeight = windowHeight * (screens.md ? 0.55 : 0.6);
      setHeight(Math.ceil(fortyPercentHeight));
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);

    return () => {
      window.removeEventListener('resize', calculateHeight);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop =
        document.documentElement.scrollTop || document.body.scrollTop;
      const clientHeight = document.documentElement.clientHeight;

      const scrollPercentage =
        (scrollTop / (scrollHeight - clientHeight)) * 100;

      if (scrollPercentage >= 15 && !scrolledPastThreshold) {
        setShowButton(true);
      } else {
        setShowButton(false);
      }

      if (scrollPercentage > 15) {
        setScrolledPastThreshold(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolledPastThreshold]);

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      await appwriteService.loginWithGoogle();
    } catch (error) {
      console.error('Error logging in with Google:', error);
    } finally {
      setGoogleLoading(false);
      setVisible((prevState) => ({
        ...prevState,
        login: false,
        signUp: false,
      }));
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleAppleLogin = async () => {
    setAppleLoading(true);
    try {
      await appwriteService.loginWithApple();
    } catch (error) {
      console.error('Error logging in with Google:', error);
    } finally {
      setAppleLoading(false);
      setVisible((prevState) => ({
        ...prevState,
        login: false,
        signUp: false,
      }));
    }
  };

  const handleFollowClick = ({
    state,
    author_id,
    author_name,
  }: {
    state: string;
    author_id: string;
    author_name: string;
  }) => {
    const eventParams: EventDetails = {
      event_name: state,
      source: 'global',
      source_type: 'author',
      source_id: author_id as string,
      source_name: author_name,
      sub_source: 'school',
      sub_source_id: data?.school_id?.toString() as string,
      sub_source_name: data?.title,
      unit: 'boolean',
      value: 1,
      meta: {
        click_source: 'school_reading_screen',
      },
    };

    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
  };

  const toggleFollow = () => {
    const newState = !isFollowing;
    handleFollowClick({
      state: newState ? 'follow' : 'unfollow',
      author_id: schoolData?.authorData?.authorId as string,
      author_name: schoolData?.authorData?.name as string,
    });
    setIsFollowing(newState);

    handleFollow({
      author_id: schoolData.authorData?.authorId as string,
      is_follower: newState,
    });
  };

  const handleScroll = () => {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const scrollTop = window.scrollY;

      const container = document.getElementById('scroll-container');
      if (container) {
        const containerHeight = container.scrollHeight - window.innerHeight;

        if (containerHeight > 0) {
          const scrollPercentage = (scrollTop / containerHeight) * 100;
          setScrollProgress(Math.round(scrollPercentage));
        }
      }
    }
  };
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleFollow = async ({
    author_id,
    is_follower,
  }: {
    author_id: string;
    is_follower: boolean;
  }) => {
    try {
      await updateFollow({
        author_id,
        is_follower,
      });
    } catch (error) {
      console.error('Error :', error);
    }
  };

  return (
    <div className={inter.className}>
      <ShouldRender check={data}>
        <Affix offsetTop={0} className='z-40 relative'>
          <div className='w-full md:bg-grayZl-950 flex px-4 md:px-6 xl:px-24 relative  bg-black justify-between  py-1.5 lg:py-2  z-40 md:py-1 md:pt-1  items-center'>
            <button
              onClick={() =>
                router.push(
                  `/schools/${schoolData.schoolSlug}/?school_id=${data?.school_id}`,
                )
              }
              className='flex items-center gap-x-2 group'
            >
              <ChevronLeftIcon className='w-4 h-4' />
              <div className='back text-white  text-xs lg:text-sm font-medium leading-5 group-hover:underline'>
                {data?.schoolName}
              </div>
            </button>

            <ShareButton
              isLoggedIN={!!token}
              noBg={screens.lg && true}
              type='school'
            />
          </div>
        </Affix>
        <div className='max-w-[1440px] justify-center mx-auto relative z-0 '>
          <div className='md:mx-6 xl:mx-24 flex '>
            <div className='flex flex-col min-h-0 flex-grow overflow-y-hidden'>
              {data && (
                <SchoolRender
                  formData={data}
                  loggedIn={loggedIn}
                  data={data}
                  schoolData={schoolData}
                  imageBlockOffset={imageBlockOffset}
                  setImageBlockOffset={setImageBlockOffset}
                />
              )}
            </div>
            <ShouldRender check={screens.lg}>
              <div
                style={{
                  marginTop: `${imageBlockOffset ? imageBlockOffset + 300 : 0}px`,
                }}
                className='w-[300px] h-full rounded-lg overflow-hidden relative flex-shrink-0'
              >
                <Affix offsetTop={100}>
                  <ShouldRender check={schoolData.advertisementPath}>
                    <div className='text-center text-sm text-[#9F9EA6] pb-3'>
                      Advertisement
                    </div>
                    <Link
                      href={schoolData?.advertisementUrl as string}
                      target='_blank'
                    >
                      <Image
                        alt='Advertising Image'
                        src={`${process.env.NEXT_PUBLIC_API_URL_IMAGE_BASE_URL_SCHOOLS}/${schoolData.advertisementPath}`}
                        width={1200}
                        height={800}
                        priority={true}
                      />
                    </Link>
                  </ShouldRender>
                </Affix>
              </div>
            </ShouldRender>
          </div>
        </div>
      </ShouldRender>
      <Affix offsetBottom={-12}>
        <div className='-bottom-0 left-0 w-full  relative z-20 customProcessPageTracking'>
          <Progress
            percent={scrollProgress}
            showInfo={false}
            size='small'
            strokeColor='#FF5612'
            className=' customProcessPageTracking w-full'
          />
        </div>
      </Affix>
      <div className='md:py-10 border-t border-[#3C3B3E] relative mt-[-10px] bg-black z-10'>
        <HomepageSlider
          loading={!!schoolData?.posts?.length}
          heading='More Chapters'
        >
          <span className='flex '>
            {schoolData?.posts?.map((item, index) => (
              <span key={`${item.schoolName}-${index}`} className='p-3'>
                <ChapterCard
                  chapter={{
                    postId: item?.postId,
                    schoolId: schoolData?.school_id as string,
                    slug: item?.slug,
                    title: item?.title,
                    postImage: item?.thumbnail,
                    reading_time: item?.reading_time,
                    reading_history: item?.reading_history,
                  }}
                  schoolData={{
                    slug: schoolData?.schoolSlug || '',
                  }}
                  index={index}
                />
              </span>
            ))}
          </span>
        </HomepageSlider>

        <LoginDrawer onCancel={onCloseLogin} visible={visibleDrawer.login} />
        <SignUpDrawer onCancel={onCloseSignUp} visible={visibleDrawer.signUp} />
      </div>
      <ShouldRender check={showButton && !loggedIn}>
        <div className={clsx('fixed bottom-0 left-0', 'w-full shadow-xl')}>
          <div
            style={{ height: `${height}px` }}
            className='bg-bgColor bg-opacity-80 p-4  backdrop-blur-sm  flex justify-center  '
          >
            <div className=' flex items-center '>
              <div className=''>
                <div className='py-3 flex flex-col gap-y-4 '>
                  <h1 className='text-lg font-semibold flex justify-center gap-x-2    mt-1 leading-10 text-fadedWhite text-center w-full'>
                    Sign up to Continue Reading
                  </h1>
                  <div className='flex justify-center'>
                    <ButtonDefault
                      size={4}
                      rounded={true}
                      variant={ButtonVariants.WHITE}
                      onClick={() => {
                        setVisibleDrawer((prevState) => ({
                          ...prevState,
                          signUp: true,
                        }));
                      }}
                      className='w-2/3 md:w-full text-center text-sm '
                    >
                      <span className='text-bold text-sm text-black py-1 w-full   font-bold whitespace-nowrap'>
                        Sign up to unlock
                      </span>
                    </ButtonDefault>
                  </div>
                </div>

                <span className='text-center font-medium flex text-sm text-white justify-center'>
                  Already have an account?
                  <p
                    onClick={() =>
                      setVisibleDrawer((prevState) => ({
                        ...prevState,
                        login: true,
                        signUp: false,
                      }))
                    }
                    className='text-primary font-bold px-1 hover:font-bold hover:underline cursor-pointer transition-all duration-300 ease-in-out'
                  >
                    Log in
                  </p>
                </span>

                <div className='pb-10 md:pb-20'>
                  <div className='flex gap-x-4 gap-y-2 justify-center pt-10 flex-col md:flex-row'>
                    <span className='flex justify-center '>
                      <ButtonDefault
                        size={4}
                        variant={ButtonVariants.UNSTYLED}
                        className='disabled:bg-bgDarkGray w-2/3 hover:bg-[#181818] disabled:text-gray-300 text-fadedWhite bg-black md:w-full  border border-bgDarkGray shadow-sm text-base'
                        rounded={true}
                        loading={googleLoading}
                        spinColor='gray'
                        disabled={googleLoading}
                        onClick={(e) => {
                          e.preventDefault();
                          handleGoogleLogin();
                        }}
                      >
                        <span className='flex text-white  text-xs items-center font-medium gap-x-3 py-1  whitespace-nowrap'>
                          <span className='w-6 h-6'>
                            <Image
                              src={GoogleIcon}
                              alt='GoogleLogo'
                              className='object-contain'
                            />
                          </span>{' '}
                          Continue with Google
                        </span>
                      </ButtonDefault>
                    </span>
                    <ShouldRender check={showAppleLogin}>
                      <span className='flex justify-center '>
                        <ButtonDefault
                          size={4}
                          variant={ButtonVariants.UNSTYLED}
                          loading={appleLoading}
                          spinColor='gray'
                          rounded={true}
                          disabled={appleLoading}
                          className='disabled:bg-bgDarkGray w-2/3 hover:bg-[#181818] disabled:text-gray-300 text-fadedWhite bg-black md:w-full  border border-bgDarkGray shadow-sm text-base'
                          onClick={(e) => {
                            e.preventDefault();
                            handleAppleLogin();
                          }}
                        >
                          <span className='flex text-white text-xs  items-center font-medium gap-x-3   whitespace-nowrap'>
                            <span className='w-5'>
                              <Image
                                src={AppleLogo}
                                alt='GoogleLogo'
                                className='object-contain'
                              />
                            </span>{' '}
                            Continue with Apple
                          </span>
                        </ButtonDefault>
                      </span>
                    </ShouldRender>
                  </div>
                  <div className='flex justify-center pt-5 md:pt-4'>
                    <div className='text-white text-xs  md:w-2/3 text-center font-normal'>
                      By using our service you are agreeing to our
                      <span className='inline-flex gap-x-1 items-baseline'>
                        <a
                          href='/terms_and_conditions.html'
                          target='_blank'
                          rel='noopener noreferrer'
                          className='text-primary font-medium cursor-pointer hover:text-darkPrimary'
                        >
                          Terms
                        </a>{' '}
                        and{' '}
                        <a
                          href='/terms_and_conditions.html'
                          target='_blank'
                          rel='noopener noreferrer'
                          className='text-primary font-medium cursor-pointer hover:text-darkPrimary'
                        >
                          conditions
                        </a>
                      </span>
                    </div>
                    <span className=' flex justify-center flex-initial '></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ShouldRender>

      <Modal
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width='400px'
        className='bg-[#212121] rounded-lg'
        closable={false}
        centered={true}
        destroyOnHidden
      >
        <div className='flex flex-col'>
          <div className='py-5'>
            Sure want to Un-Follow {schoolData?.authorData?.name} ?
          </div>
          <div className='flex gap-x-1 justify-end'>
            <span>
              <Button onClick={handleCancel} type='text'>
                No
              </Button>
            </span>
            <span>
              <Button
                onClick={() => {
                  toggleFollow();
                  setIsModalVisible(false);
                }}
                type='text'
                className='text-blue-500 font-medium'
              >
                Yes
              </Button>
            </span>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ChaptersPage;
